export const AUTOFILL_ANIMATION = 'onAutofillSyncStart'

export function syncAutofillInput(input) {
  if (!input?.dispatchEvent) return
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

export function needsAutofillSync(input) {
  if (!input?.value) return false

  const field = input.closest('.v-field')
  if (field?.classList.contains('v-field--dirty')) return false

  try {
    if (input.matches(':-webkit-autofill') || input.matches(':autofill')) return true
  } catch {
    // Unsupported selector in this browser.
  }

  // Password managers may fill values without exposing autofill pseudo-classes.
  return Boolean(field)
}

export function observeAutofillInput(input) {
  const onAnimation = (event) => {
    if (event.animationName === AUTOFILL_ANIMATION) syncAutofillInput(input)
  }

  input.addEventListener('animationstart', onAnimation)

  const pollDelays = [0, 100, 300, 600, 1000]
  const timeouts = pollDelays.map((delay) =>
    setTimeout(() => {
      if (needsAutofillSync(input)) syncAutofillInput(input)
    }, delay)
  )

  return () => {
    input.removeEventListener('animationstart', onAnimation)
    timeouts.forEach(clearTimeout)
  }
}

export function observeAutofillContainer(container) {
  if (!container) return () => {}

  const cleanups = new Map()

  const attach = (input) => {
    if (cleanups.has(input)) return
    cleanups.set(input, observeAutofillInput(input))
  }

  const detach = (input) => {
    cleanups.get(input)?.()
    cleanups.delete(input)
  }

  container.querySelectorAll('input').forEach(attach)

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return
        if (node.matches?.('input')) attach(node)
        node.querySelectorAll?.('input').forEach(attach)
      })
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return
        if (node.matches?.('input')) detach(node)
        node.querySelectorAll?.('input').forEach(detach)
      })
    }
  })

  observer.observe(container, { childList: true, subtree: true })

  return () => {
    observer.disconnect()
    cleanups.forEach((cleanup) => cleanup())
    cleanups.clear()
  }
}
